<template>
  <div class="landing-page">
    <div class="top-bar">
      <img class="top-bar-logo" src="./assets/SCRA.png" alt="SCRA" />
      <a href="https://nsf.elsevierpure.com/en/projects/sttr-phase-blockchain-ontology-and-services-for-monitoring-and-pe/" target="_blank" rel="noopener noreferrer" class="top-bar-logo-link" title="NSF STTR Phase I: Blockchain ontology and services for monitoring and performing analytics on smart contracts">
        <img class="top-bar-logo" src="./assets/NSF.svg.png" alt="NSF" />
      </a>
      <a href="https://twitter.com/glossifi" class="top-bar-twitter" target="_blank" rel="noopener noreferrer">
        <img class="image3" src="./assets/twitter2.png" alt="Twitter" />
      </a>
    </div>

    <!-- Hero -->
    <div class="section welcome">
      <h1 class="hero-brand">Glossi.fi</h1>
      <h2 class="hero-headline">Capital-native coordination for digital operational risk.</h2>
      <p class="subhead">Glossifi makes protocol failure surfaces legible to capital so security work is funded continuously, not episodically.</p>
      <p class="microline">Long-horizon incentives for prevention, not reactive security theater.</p>
      <a class="gv3-link" href="/gv3/">Launch GV3 App</a>
    </div>

    <div class="gradient-band"></div>

    <!-- Below the fold: 6 sections -->
    <div class="content-area">
    <div class="section services">
      <div class="service-boxes">
        <div class="service-box">
          <h3>What it is</h3>
          <p>Glossifi is a coordination layer for security funding. It turns protocol failure surfaces into first-class objects capital can attach to, so security work is funded continuously instead of only after incidents.</p>
          <p>It makes fragility legible, routes incentives toward prevention, and creates a persistent economic signal around where systems are most exposed.</p>
        </div>
        <div class="service-box">
          <h3>How it works</h3>
          <p>Protocols expose specific failure surfaces: bridges, oracles, upgrade paths, liquidation logic, privileged roles, and compositional dependencies. Glossifi treats these surfaces as objects rather than background risk.</p>
          <p>Capital allocates into structured exposure tied to those surfaces. That allocation creates sustained attention and directs rewards toward security work that measurably reduces fragility over time. Performance is evaluated across epochs, not just at the moment something breaks.</p>
          <p>Early coordination is bootstrapped from a finite rewards pool to seed participation and surface signal. Over time, the system is designed to transition toward security-native and protocol-native cash flows so long-horizon participation is not sustained by open-ended token inflation.</p>
        </div>
        <div class="service-box">
          <h3>Why it matters</h3>
          <p>Most security funding is reactive. Attention spikes after incidents, then fades. That pattern is predictable and exploitable.</p>
          <p>Long-horizon incentives change what kinds of security work become viable. When prevention is funded continuously, teams can invest in slow, unglamorous work that actually reduces systemic risk instead of chasing post-mortem bounties.</p>
        </div>
        <div class="service-box">
          <h3>The instrument<br />(for capital allocators)</h3>
          <p>The primary financial primitive is a long-horizon security coordination instrument. It has some bond-like intuitions in that capital is committed over time and earns yield for sustained participation.</p>
          <p>Returns are sourced from security-native and protocol-native cash flows over time, not from open-ended token emissions. Early rewards are bootstrapped from a finite pool to seed coordination and discovery. The instrument exists to align capital allocators with security researchers around prevention over time, not to create short-term yield opportunities.</p>
        </div>
        <div class="service-box">
          <h3>Institutional legibility</h3>
          <p>Finance already allocates to operational risk. Catastrophe risk, credit risk, and reinsurance exist to route tail risk through capital markets.</p>
          <p>Digital operational risk is still treated as an externality. Glossifi structures it as an allocatable surface so it can be reasoned about, priced, and learned from over time. The goal is to move systems towards safety by making fragility legible to capital in a continuous, adversarial environment.</p>
        </div>
        <div class="service-box">
          <h3>The feedback loop</h3>
          <p>Allocations surface where fragility concentrates. Rewards surface what security work actually helps. Over time, the system learns where attention is thin, where it is mispriced, and where incentives are misaligned.</p>
          <p>That feedback flows back into monitoring, prioritization, and future security design, creating a loop where capital, labor, and risk surfaces co-evolve instead of reacting after failures.</p>
        </div>
      </div>
    </div>

    <br />

    <!-- Contact -->
    <div class="section contact">
      <div class="contact-simple">
        <p class="contact-title">Partners &amp; Funds</p>
        <p class="contact-email">Contact us at <a href="mailto:admin@glossi.fi" class="contact-link">admin@glossi.fi</a></p>
      </div>
    </div>

    <!-- Footer -->
    <div class="footer">
      <div class="footer-identity">
        <strong>Glossifi</strong><br />
        Donald Josey · <a href="https://twitter.com/glossifi" class="footer-link">@glossifi</a> · <a href="https://www.linkedin.com/company/79389799/admin/dashboard/" target="_blank" rel="noopener noreferrer" class="footer-link">LinkedIn</a>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LandingPage',
};
</script>

<style scoped>
.landing-page {
  display: flex;
  flex-direction: column;
  background: #1aee41;
  width: 100%;
  overflow: hidden;
  position: relative;
  min-height: 100vh;
}

.gradient-band {
  height: 70vh;
  min-height: 400px;
  background: linear-gradient(to bottom, #1aee41 0%, #0d260d 40%, #000000 100%);
  flex-shrink: 0;
}

.content-area {
  background: #000000;
  flex: 1;
}

.section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.top-bar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  background: #1aee41;
}
.top-bar-logo {
  height: 36px;
  width: auto;
  object-fit: contain;
}
.top-bar-logo-link {
  display: flex;
  align-items: center;
  text-decoration: none;
}
.top-bar-logo-link .top-bar-logo {
  display: block;
}
.top-bar-twitter {
  display: flex;
  align-items: center;
  text-decoration: none;
}
.top-bar-twitter .image3 {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.welcome {
  height: 100vh;
  padding: 0 1.5rem;
  box-sizing: border-box;
  background: #1aee41;
}
.hero-brand {
  font-size: 5rem;
  margin: 0 0 0.5rem;
  letter-spacing: 0.02em;
  font-weight: 700;
}
.hero-headline {
  font-size: 2.5rem;
  max-width: 20em;
  margin: 0 auto 0.75rem;
  line-height: 1.2;
  font-weight: 600;
}
.welcome .subhead {
  color: #120;
  font-weight: bold;
  font-size: 1.25rem;
  max-width: 28em;
  margin: 0 auto 1.5rem;
  line-height: 1.4;
}
.hero-ctas {
  margin: 0.5rem 0;
}
.cta-link {
  color: #120;
  font-weight: bold;
  text-decoration: underline;
}
.cta-link:hover {
  text-decoration: none;
}
.cta-sep {
  margin: 0 0.5rem;
  color: #120;
}
.microline {
  margin-top: 1rem;
  font-size: 0.95rem;
  color: #120;
  font-style: italic;
  max-width: 24em;
  margin-left: auto;
  margin-right: auto;
}
.gv3-link {
  display: none !important;
  margin-top: 1.5rem;
  padding: 0.7rem 1.2rem;
  border-radius: 8px;
  background: #000;
  color: #1aee41;
  text-decoration: none;
  font-weight: 700;
  letter-spacing: 0.02em;
  border: 2px solid #000;
  transition: all 0.2s ease;
}
.gv3-link:hover {
  background: transparent;
  color: #120;
}

@media (max-width: 768px) {
  .top-bar-logo {
    height: 28px;
  }
  .top-bar-twitter .image3 {
    width: 28px;
    height: 28px;
  }
  .welcome {
    height: auto;
    min-height: 80vh;
    padding: 3rem 1rem;
  }
  .hero-brand {
    font-size: 3rem;
  }
  .hero-headline {
    font-size: 1.75rem;
  }
  .welcome .subhead {
    font-size: 1rem;
  }
  .microline {
    font-size: 0.85rem;
  }
}

.services {
  color: #fff;
  padding: 2rem 1rem 3rem;
  min-height: auto;
}
.service-boxes {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  margin-top: 20px;
  max-width: 2100px;
}
.service-box {
  width: calc(33.33% - 30px);
  padding: 20px;
  background-color: #1aee41;
  margin: 10px;
  border-radius: 10px;
  text-align: left;
  color: #120;
  box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease-in-out;
}
.service-box h3 {
  text-align: center;
  margin-top: 0;
  color: #120;
}
.service-box p {
  color: #120;
  font-weight: bold;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0.75em 0;
}
.service-box p:first-of-type {
  margin-top: 0;
}

@media (max-width: 768px) {
  .service-box {
    width: calc(100% - 20px);
    max-width: 90%;
  }
}

.contact {
  position: relative;
  color: #fff;
  height: 100%;
  padding: 2rem 0;
}

.contact-simple {
  text-align: center;
  max-width: 480px;
  margin: 0 auto;
}
.contact-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.contact-email {
  font-size: 1rem;
  margin: 0;
}
.contact-link {
  color: #1aee41;
  text-decoration: underline;
  font-weight: 500;
}
.contact-link:hover {
  text-decoration: none;
}

.footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 10px 1.5rem;
  color: #fff;
  gap: 1rem;
}
.footer-identity {
  text-align: center;
  font-size: 0.9rem;
  line-height: 1.6;
}
.footer-link {
  color: #fff;
  text-decoration: underline;
}
.footer-link:hover {
  text-decoration: none;
}
.footer-ctas {
  margin: 0.25rem 0;
}
.footer-ctas .cta-link {
  color: #fff;
}

.image3 {
  width: 100%;
}


input,
textarea {
  width: 100%;
  margin: 5px 0;
}
</style>
